import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Header from '../../components/ui/Header';
import AdminSidebar from '../../components/ui/AdminSidebar';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
// Data will come from Supabase
const modulesData = [];
const lessonsData = [];

const ContentModuleEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [isLoading, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    cover: '',
    summary: '',
    learningObjectives: '',
    prerequisites: [],
    status: 'available',
    publishDate: '',
    seoTitle: '',
    seoDescription: '',
    lessons: [],
    resources: [],
    externalLinks: [],
    visibility: 'public',
    enrollmentRequired: true,
    completionCriteria: 'all_lessons'
  });

  const [errors, setErrors] = useState({});
  const [lessonsList, setLessonsList] = useState([]);

  const breadcrumbItems = [
    { label: 'Admin', path: '/admin-panel-dashboard' },
    { label: 'Conteúdo', path: '/admin/modules' },
    { label: 'Módulos', path: '/admin/modules' },
    { label: isEditMode ? 'Editar Módulo' : 'Novo Módulo', path: '', isActive: true }
  ];

  const tabs = [
    { id: 'details', label: 'Detalhes Básicos', icon: 'FileText' },
    { id: 'structure', label: 'Estrutura de Lições', icon: 'List' },
    { id: 'resources', label: 'Recursos', icon: 'Paperclip' },
    { id: 'settings', label: 'Configurações', icon: 'Settings' }
  ];

  const mockPrerequisites = [
    { id: 1, title: 'Fundamentos de Marketing Digital' },
    { id: 2, title: 'Introdução ao E-commerce' },
    { id: 3, title: 'Básico de Analytics' }
  ];

  // Load existing module data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const module = modulesData?.find(m => m?.id === parseInt(id));
      if (module) {
        setFormData({
          title: module?.title || '',
          slug: module?.slug || '',
          cover: module?.cover || '',
          summary: module?.summary || '',
          learningObjectives: module?.learningObjectives || '',
          prerequisites: module?.prerequisites || [],
          status: module?.status || 'available',
          publishDate: module?.publishDate || '',
          seoTitle: module?.seoTitle || '',
          seoDescription: module?.seoDescription || '',
          lessons: module?.lessons || [],
          resources: module?.resources || [],
          externalLinks: module?.externalLinks || [],
          visibility: module?.visibility || 'public',
          enrollmentRequired: module?.enrollmentRequired ?? true,
          completionCriteria: module?.completionCriteria || 'all_lessons'
        });

        // Load associated lessons
        const moduleLessons = lessonsData?.filter(l => l?.moduleId === parseInt(id)) || [];
        setLessonsList(moduleLessons);
      }
    }
  }, [isEditMode, id]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEditMode && formData?.title) {
      const slug = formData?.title
        ?.toLowerCase()
        ?.replace(/[^\w\s-]/g, '')
        ?.replace(/\s+/g, '-')
        ?.trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData?.title, isEditMode]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.title?.trim()) newErrors.title = 'Título é obrigatório';
    if (!formData?.slug?.trim()) newErrors.slug = 'Slug é obrigatório';
    if (!formData?.summary?.trim()) newErrors.summary = 'Resumo é obrigatório';
    if (!formData?.status) newErrors.status = 'Status é obrigatório';
    
    if (formData?.slug && !/^[a-z0-9-]+$/?.test(formData?.slug)) {
      newErrors.slug = 'Slug deve conter apenas letras minúsculas, números e hífens';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePrerequisiteToggle = (prereqId) => {
    const prerequisites = formData?.prerequisites || [];
    const newPrerequisites = prerequisites?.includes(prereqId)
      ? prerequisites?.filter(id => id !== prereqId)
      : [...prerequisites, prereqId];
    handleInputChange('prerequisites', newPrerequisites);
  };

  const handleAddResource = () => {
    const newResource = {
      id: Date.now(),
      title: '',
      type: 'file',
      url: '',
      description: ''
    };
    handleInputChange('resources', [...(formData?.resources || []), newResource]);
  };

  const handleRemoveResource = (resourceId) => {
    const updatedResources = formData?.resources?.filter(r => r?.id !== resourceId);
    handleInputChange('resources', updatedResources);
  };

  const handleAddExternalLink = () => {
    const newLink = {
      id: Date.now(),
      title: '',
      url: '',
      description: ''
    };
    handleInputChange('externalLinks', [...(formData?.externalLinks || []), newLink]);
  };

  const handleRemoveExternalLink = (linkId) => {
    const updatedLinks = formData?.externalLinks?.filter(l => l?.id !== linkId);
    handleInputChange('externalLinks', updatedLinks);
  };

  const handleLessonDragEnd = (result) => {
    if (!result?.destination) return;

    const items = Array.from(lessonsList);
    const [reorderedItem] = items?.splice(result?.source?.index, 1);
    items?.splice(result?.destination?.index, 0, reorderedItem);

    // Update lesson order
    const updatedLessons = items?.map((lesson, index) => ({
      ...lesson,
      order: index + 1
    }));

    setLessonsList(updatedLessons);
  };

  const calculateEstimatedDuration = () => {
    const totalMinutes = lessonsList?.reduce((total, lesson) => {
      const duration = lesson?.duration || '0:00';
      const [minutes, seconds] = duration?.split(':')?.map(Number);
      return total + minutes + (seconds / 60);
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      showToast('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const moduleData = {
        id: isEditMode ? parseInt(id) : Date.now(),
        ...formData,
        lessonsCount: lessonsList?.length || 0,
        estimatedDuration: calculateEstimatedDuration(),
        updatedAt: new Date()?.toISOString(),
        createdAt: isEditMode ? formData?.createdAt : new Date()?.toISOString()
      };

      // Update local JSON data (mock persistence)
      if (isEditMode) {
        const updatedModules = modulesData?.map(module =>
          module?.id === parseInt(id) ? moduleData : module
        );
        console.log('Updated module:', moduleData);
      } else {
        modulesData?.push(moduleData);
        console.log('Created module:', moduleData);
      }

      // Update lessons with new module reference
      lessonsList?.forEach(lesson => {
        lesson.moduleId = moduleData?.id;
      });

      showToast(`Módulo ${isEditMode ? 'atualizado' : 'criado'} com sucesso!`, 'success');
      navigate('/admin/modules');
    } catch (error) {
      showToast('Erro ao salvar módulo. Tente novamente.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showToast = (message, type) => {
    // Mock toast notification
    const toastElement = document.createElement('div');
    toastElement.className = `fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg ${
      type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    }`;
    toastElement.textContent = message;
    document.body?.appendChild(toastElement);
    
    setTimeout(() => {
      document.body?.removeChild(toastElement);
    }, 3000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Título do Módulo *"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                error={errors?.title}
                placeholder="Digite o título do módulo"
              />
              <Input
                label="Slug *"
                value={formData?.slug}
                onChange={(e) => handleInputChange('slug', e?.target?.value)}
                error={errors?.slug}
                placeholder="url-amigavel-do-modulo"
                helperText="URL amigável para o módulo (apenas letras minúsculas, números e hífens)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Capa do Módulo
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                {formData?.cover ? (
                  <div className="relative">
                    <img
                      src={formData?.cover}
                      alt="Capa do módulo"
                      className="max-h-40 mx-auto rounded-lg"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInputChange('cover', '')}
                      className="mt-2"
                    >
                      Remover
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Upload de imagem de capa (1200x600px recomendado)
                    </p>
                    <Button variant="outline" iconName="Upload" iconPosition="left">
                      Selecionar Imagem
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Resumo do Módulo *
              </label>
              <textarea
                value={formData?.summary}
                onChange={(e) => handleInputChange('summary', e?.target?.value)}
                placeholder="Descreva brevemente o que será ensinado neste módulo..."
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background ${
                  errors?.summary ? 'border-red-500' : 'border-border'
                }`}
              />
              {errors?.summary && (
                <p className="text-red-500 text-sm mt-1">{errors?.summary}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Objetivos de Aprendizagem
              </label>
              <textarea
                value={formData?.learningObjectives}
                onChange={(e) => handleInputChange('learningObjectives', e?.target?.value)}
                placeholder="Liste os principais objetivos de aprendizagem..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Pré-requisitos
              </label>
              <div className="space-y-2">
                {mockPrerequisites?.map((prereq) => (
                  <label key={prereq?.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent/50">
                    <input
                      type="checkbox"
                      checked={formData?.prerequisites?.includes(prereq?.id)}
                      onChange={() => handlePrerequisiteToggle(prereq?.id)}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-sm text-foreground">{prereq?.title}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'structure':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-foreground">Estrutura de Lições</h3>
                <p className="text-sm text-muted-foreground">
                  Organize as lições do módulo por meio de arrastar e soltar
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-muted-foreground">
                  {lessonsList?.length} lições • {calculateEstimatedDuration()}
                </span>
                <Button variant="outline" iconName="Plus" iconPosition="left">
                  Nova Lição
                </Button>
              </div>
            </div>

            <DragDropContext onDragEnd={handleLessonDragEnd}>
              <Droppable droppableId="lessons">
                {(provided) => (
                  <div {...provided?.droppableProps} ref={provided?.innerRef} className="space-y-3">
                    {lessonsList?.map((lesson, index) => (
                      <Draggable key={lesson?.id} draggableId={lesson?.id?.toString()} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided?.innerRef}
                            {...provided?.draggableProps}
                            className={`bg-card border border-border rounded-lg p-4 ${
                              snapshot?.isDragging ? 'shadow-lg' : ''
                            }`}
                          >
                            <div className="flex items-center space-x-4">
                              <div
                                {...provided?.dragHandleProps}
                                className="cursor-grab hover:cursor-grabbing"
                              >
                                <Icon name="GripVertical" size={20} className="text-muted-foreground" />
                              </div>

                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h4 className="font-medium text-foreground">{lesson?.title}</h4>
                                    <p className="text-sm text-muted-foreground">{lesson?.description}</p>
                                  </div>
                                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                                    <Icon name="Clock" size={14} />
                                    <span>{lesson?.duration || '0:00'}</span>
                                  </div>
                                </div>

                                {lesson?.activities?.length > 0 && (
                                  <div className="mt-2 flex items-center space-x-2">
                                    <Icon name="CheckSquare" size={14} className="text-blue-600" />
                                    <span className="text-xs text-blue-600">
                                      {lesson?.activities?.length} atividade(s)
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center space-x-1">
                                <Button variant="ghost" size="sm" iconName="Edit" />
                                <Button variant="ghost" size="sm" iconName="Trash2" />
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided?.placeholder}

                    {lessonsList?.length === 0 && (
                      <div className="text-center py-12">
                        <Icon name="BookOpen" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          Nenhuma lição adicionada
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          Comece adicionando a primeira lição do módulo
                        </p>
                        <Button variant="outline" iconName="Plus" iconPosition="left">
                          Adicionar Primeira Lição
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        );

      case 'resources':
        return (
          <div className="space-y-6">
            {/* Media Library Integration */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Biblioteca de Mídia</h3>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground">Anexar mídia da biblioteca</span>
                  <Button variant="outline" iconName="Image" iconPosition="left" size="sm">
                    Procurar Mídia
                  </Button>
                </div>
              </div>
            </div>

            {/* Downloadable Resources */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">Materiais para Download</h3>
                <Button variant="outline" iconName="Plus" iconPosition="left" onClick={handleAddResource}>
                  Adicionar Recurso
                </Button>
              </div>

              <div className="space-y-3">
                {formData?.resources?.map((resource, index) => (
                  <div key={resource?.id} className="bg-card border border-border rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        placeholder="Nome do recurso"
                        value={resource?.title}
                        onChange={(e) => {
                          const updatedResources = [...formData?.resources];
                          updatedResources[index].title = e?.target?.value;
                          handleInputChange('resources', updatedResources);
                        }}
                      />
                      <Select
                        value={resource?.type}
                        onValueChange={(value) => {
                          const updatedResources = [...formData?.resources];
                          updatedResources[index].type = value;
                          handleInputChange('resources', updatedResources);
                        }}
                        placeholder="Tipo"
                      >
                        <option value="file">Arquivo</option>
                        <option value="video">Vídeo</option>
                        <option value="audio">Áudio</option>
                        <option value="document">Documento</option>
                      </Select>
                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="URL do recurso"
                          value={resource?.url}
                          onChange={(e) => {
                            const updatedResources = [...formData?.resources];
                            updatedResources[index].url = e?.target?.value;
                            handleInputChange('resources', updatedResources);
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleRemoveResource(resource?.id)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* External Links */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">Links Externos</h3>
                <Button variant="outline" iconName="Plus" iconPosition="left" onClick={handleAddExternalLink}>
                  Adicionar Link
                </Button>
              </div>

              <div className="space-y-3">
                {formData?.externalLinks?.map((link, index) => (
                  <div key={link?.id} className="bg-card border border-border rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        placeholder="Título do link"
                        value={link?.title}
                        onChange={(e) => {
                          const updatedLinks = [...formData?.externalLinks];
                          updatedLinks[index].title = e?.target?.value;
                          handleInputChange('externalLinks', updatedLinks);
                        }}
                      />
                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="https://exemplo.com"
                          value={link?.url}
                          onChange={(e) => {
                            const updatedLinks = [...formData?.externalLinks];
                            updatedLinks[index].url = e?.target?.value;
                            handleInputChange('externalLinks', updatedLinks);
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleRemoveExternalLink(link?.id)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status *
                </label>
                <Select
                  value={formData?.status}
                  onValueChange={(value) => handleInputChange('status', value)}
                  placeholder="Selecione o status"
                >
                  <option value="available">Disponível</option>
                  <option value="coming_soon">Em Breve</option>
                  <option value="completed">Concluído</option>
                  <option value="draft">Rascunho</option>
                </Select>
                {errors?.status && (
                  <p className="text-red-500 text-sm mt-1">{errors?.status}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Visibilidade
                </label>
                <Select
                  value={formData?.visibility}
                  onValueChange={(value) => handleInputChange('visibility', value)}
                  placeholder="Selecione a visibilidade"
                >
                  <option value="public">Público</option>
                  <option value="private">Privado</option>
                  <option value="restricted">Restrito</option>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Data de Publicação
              </label>
              <Input
                type="datetime-local"
                value={formData?.publishDate}
                onChange={(e) => handleInputChange('publishDate', e?.target?.value)}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="enrollment-required"
                  checked={formData?.enrollmentRequired}
                  onChange={(e) => handleInputChange('enrollmentRequired', e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                />
                <label htmlFor="enrollment-required" className="text-sm text-foreground">
                  Requer inscrição para acessar
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Critério de Conclusão
              </label>
              <Select
                value={formData?.completionCriteria}
                onValueChange={(value) => handleInputChange('completionCriteria', value)}
                placeholder="Selecione o critério"
              >
                <option value="all_lessons">Todas as lições</option>
                <option value="percentage">Porcentagem mínima</option>
                <option value="activities">Atividades obrigatórias</option>
                <option value="manual">Aprovação manual</option>
              </Select>
            </div>

            {/* SEO Settings */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-medium text-foreground mb-4">SEO</h3>
              <div className="space-y-4">
                <Input
                  label="Título SEO"
                  value={formData?.seoTitle}
                  onChange={(e) => handleInputChange('seoTitle', e?.target?.value)}
                  placeholder="Título otimizado para SEO"
                  maxLength={60}
                  helperText={`${formData?.seoTitle?.length || 0}/60 caracteres`}
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Descrição SEO
                  </label>
                  <textarea
                    value={formData?.seoDescription}
                    onChange={(e) => handleInputChange('seoDescription', e?.target?.value)}
                    placeholder="Descrição otimizada para SEO"
                    rows={3}
                    maxLength={160}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    {formData?.seoDescription?.length || 0}/160 caracteres
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (previewMode) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="container mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold">Preview do Módulo</h1>
              <Button
                variant="outline"
                onClick={() => setPreviewMode(false)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Voltar ao Editor
              </Button>
            </div>

            <div className="bg-card rounded-lg border border-border p-8">
              <div className="max-w-4xl mx-auto">
                {formData?.cover && (
                  <img
                    src={formData?.cover}
                    alt={formData?.title}
                    className="w-full h-64 object-cover rounded-lg mb-6"
                  />
                )}

                <h1 className="text-4xl font-bold text-foreground mb-4">
                  {formData?.title || 'Título do Módulo'}
                </h1>

                <p className="text-lg text-muted-foreground mb-6">
                  {formData?.summary || 'Resumo do módulo...'}
                </p>

                {formData?.learningObjectives && (
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground mb-3">
                      Objetivos de Aprendizagem
                    </h2>
                    <p className="text-foreground">{formData?.learningObjectives}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Informações do Módulo</h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>Duração estimada: {calculateEstimatedDuration()}</li>
                      <li>Total de lições: {lessonsList?.length}</li>
                      <li>Status: {formData?.status}</li>
                    </ul>
                  </div>

                  {formData?.prerequisites?.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Pré-requisitos</h3>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        {formData?.prerequisites?.map(prereqId => {
                          const prereq = mockPrerequisites?.find(p => p?.id === prereqId);
                          return prereq ? <li key={prereq?.id}>{prereq?.title}</li> : null;
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="page-module-editor">
      <Header />
      <AdminSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      }`}>
        <div className="p-6">
          <Breadcrumb customItems={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                {isEditMode ? 'Editar Módulo' : 'Novo Módulo'}
              </h1>
              <p className="text-muted-foreground font-caption">
                {isEditMode ? 'Atualize o conteúdo educacional' : 'Crie um novo módulo de aprendizagem'}
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => setPreviewMode(true)}
                iconName="Eye"
                iconPosition="left"
              >
                Visualizar
              </Button>
              <Button variant="outline" iconName="Save" iconPosition="left">
                Salvar Rascunho
              </Button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="max-w-6xl">
            {/* Tab Navigation */}
            <div className="bg-card rounded-lg border border-border mb-6">
              <div className="border-b border-border">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      type="button"
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} className="mr-2" />
                      {tab?.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {renderTabContent()}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-between bg-card rounded-lg border border-border p-6">
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/admin/modules')}
                >
                  Cancelar
                </Button>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleInputChange('status', 'draft')}
                  disabled={formData?.status === 'draft'}
                >
                  Salvar como Rascunho
                </Button>
                <Button
                  type="submit"
                  loading={isLoading}
                  iconName="BookOpen"
                  iconPosition="left"
                  data-testid="btn-create-module"
                  className="bg-yellow-500 hover:bg-yellow-600"
                >
                  {isEditMode ? 'Atualizar Módulo' : 'Criar Módulo'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default ContentModuleEditor;